import type React from 'react';
import { Badge as TailGridsBadge } from '../tailgrids/core/badge.js';
import { cn } from '../../utils/cn.js';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info' | 'accent' | 'purple' | 'teal';

/** Our semantic tones on NextAdmin's badge palette. */
const COLOR: Record<BadgeVariant, 'success' | 'warning' | 'error' | 'gray' | 'sky' | 'primary' | 'purple' | 'cyan'> = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
  neutral: 'gray',
  info: 'sky',
  accent: 'primary',
  purple: 'purple',
  teal: 'cyan',
};

interface BadgeProps {
  variant: BadgeVariant;
  children: React.ReactNode;
  icon?: React.ReactNode;
  /** Leading status dot in the badge's own color. */
  dot?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/** A short status or category label. For a status vocabulary, map codes to labels once — never show raw codes. */
export function Badge({ variant, children, icon, dot, size = 'sm', className }: BadgeProps) {
  return (
    <TailGridsBadge
      data-variant={variant}
      color={COLOR[variant]}
      size={size}
      className={cn('w-fit max-w-full shrink-0 self-center whitespace-nowrap', className)}
      prefixIcon={
        dot ? (
          <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
        ) : icon ? (
          <span className="inline-flex shrink-0 items-center [&>svg]:size-3.5" aria-hidden="true">
            {icon}
          </span>
        ) : undefined
      }
    >
      {children}
    </TailGridsBadge>
  );
}
